import { ECardType } from '__generated__/globalTypes';
import { DetailCards } from 'components/detail/view/DetailCards';
import { LinkedDetailsCard } from 'components/detail/view/DetailCards/LinkedDetailsCard';
import { DetailHeader } from 'components/detail/view/DetailHeader';
import { MainButton } from 'elements/button/MainButton';
import { motion } from 'framer-motion';
import { useAnchorScroll } from 'hooks/general/useAnchorScroll';
import { useAnalytics } from 'libs/utils/site/analytics';
import { intersection, uniq } from 'lodash';
import { EVPcScrollAnchors } from 'pages/EventViewPage/EventViewPagePC/eVScrollAnchors';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    detailId: string;
}

export const EventViewEntityPageContent = (props: Props) => {
    const ctxEventView = useContext(CtxEventView);
    const navigate = useNavigate();
    const isMobile = !ctxEventView.isWide;
    const [searchParams, setSearchParams] = useSearchParams();
    const { scrollToAnchor } = useAnchorScroll();
    const analytics = useAnalytics();

    const { eventPath, allStories } = ctxEventView;

    const story = allStories.find((e) => e.storyId === props.detailId);

    if (!story) {
        return;
    }

    // match ctxEventView categories based on story.categories
    const categories = story.categoryIds.map((categoryId) => {
        return ctxEventView.categories.find((category) => category.categoryId === categoryId);
    });

    function handleClickShowOnMap() {
        analytics.track('event_ui.entity.go_to_marker.clicked', {
            markerId: story.markerId,
            storyId: story.storyId,
            title: story.storyHeader?.title
        });

        if (ctxEventView.mapEntityState.filterIds?.length > 0) {
            if (
                intersection(ctxEventView.mapEntityState.filterIds, story.categoryIds).length === 0
            ) {
                ctxEventView.mapEntityState.setFilterIds(null);
            }
        }

        if (isMobile) {
            navigate(`/event/${eventPath}/map?markerId=${story.markerId}`);
        } else {
            searchParams.set('markerId', story.markerId);
            searchParams.set('zoomTo', '1');
            setSearchParams(searchParams);
            scrollToAnchor(EVPcScrollAnchors.Map);
        }
    }

    function getSpeakerCard() {
        const showSpeakersEnabled = !!ctxEventView.eventData.programSettings?.speakersListId;

        if (!showSpeakersEnabled) {
            return null;
        }

        const speakerList = ctxEventView.otherEntityDefinitions.find(
            (e) => e.id === ctxEventView.eventData.programSettings?.speakersListId
        );

        if (!speakerList) {
            return null;
        }

        const isInSpeakerList = speakerList.list.map((e) => e.storyId).includes(props.detailId);

        if (isInSpeakerList) {
            return null;
        }

        // Here it would be ideal to get only a single program by programId
        // but let's leave that for later
        const programData = ctxEventView.allPrograms.filter((e) =>
            e.storyIds.includes(props.detailId)
        );

        if (!programData.length) {
            return null;
        }

        const speakersStories = uniq(programData.flatMap((e) => e.storyIds))
            .filter((storyId) => speakerList.list.some((e) => storyId === e.storyId))
            .map((speakerId) => ctxEventView.allStories?.find((e) => e.storyId === speakerId));

        if (!speakersStories.length) {
            return null;
        }

        const speakersCard = {
            title: speakerList.name,
            speakerIds: speakersStories.map((speakerDetail) => speakerDetail.storyId)
        };

        return speakersCard;
    }

    function getProgramIds() {
        const showProgramEnabled = !!ctxEventView.eventData.programSettings?.speakersListId;

        if (!showProgramEnabled) {
            return null;
        }

        const speakerList = ctxEventView.otherEntityDefinitions.find(
            (e) => e.id === ctxEventView.eventData.programSettings?.speakersListId
        );

        if (!speakerList) {
            return null;
        }

        const isInSpeakerList = speakerList.list.map((e) => e.storyId).includes(props.detailId);

        if (!isInSpeakerList) {
            return null;
        }

        const programData = ctxEventView.allPrograms.filter((e) =>
            e.storyIds.includes(props.detailId)
        );

        if (!programData.length) {
            return null;
        }

        return programData.map((e) => e.programId);
    }

    const speakerCard = getSpeakerCard();

    return (
        <S_EVEntityPageContent className={props.className}>
            <DetailHeader
                header={story.storyHeader}
                skipTitle={true}
                categories={categories?.map((category) => category?.title)}
            >
                {story.markerId && !ctxEventView.isEmbed && (
                    <S_DetailHeaderContent>
                        <MainButton
                            iconSet="far"
                            iconName="location-dot"
                            type="event"
                            onClick={handleClickShowOnMap}
                        >
                            {Localization.SHOW_ON_MAP}
                        </MainButton>
                    </S_DetailHeaderContent>
                )}
            </DetailHeader>
            <DetailCards cards={story.storyCards} programIds={getProgramIds()}>
                {speakerCard && (
                    <LinkedDetailsCard
                        card={{
                            cardId: 'speakers',
                            info: { title: speakerCard.title, __typename: 'CardInfo' },
                            linkedStories: {
                                storyIds: speakerCard?.speakerIds,
                                __typename: 'LinkedStories'
                            },
                            type: ECardType.LINKEDSTORIES,
                            __typename: 'LinkedStoriesCard'
                        }}
                    />
                )}
            </DetailCards>
        </S_EVEntityPageContent>
    );
};

const S_EVEntityPageContent = styled(motion.div)`
    width: 100%;
    background: #f6f9ff;
`;

const S_DetailHeaderContent = styled.div`
    display: flex;
    width: 100;
    justify-content: right;
`;
